import React, { useState, useEffect } from 'react';
import { usePageTitle } from '../utils/usePageTitle';
import { Routes, Route, NavLink, useNavigate } from 'react-router-dom';
import api from '../services/api';
import Overview from '../components/Overview';
import Opportunities from '../components/Opportunities';
import Keywords from '../components/Keywords';
import Brief from '../components/Brief';
import MonthlyReport from '../components/MonthlyReport';
import FunnelReport from '../components/FunnelReport';
import CombinedReport from '../components/CombinedReport';
import Settings from '../components/Settings';
import SiteAudit from '../components/SiteAudit';
import RankingHistory from '../components/RankingHistory';
import BackfillBanner from '../components/BackfillBanner';
import Competitors from '../components/Competitors';
import './Dashboard.css';

function Dashboard({ onLogout }) {
  const [websites, setWebsites] = useState([]);
  const [currentWebsite, setCurrentWebsite] = useState(null);
  const [globalDays, setGlobalDays] = useState(28);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadWebsites();
  }, []);

  const loadWebsites = async () => {
    try {
      const data = await api.getWebsites();
      const list = data.websites || [];
      setWebsites(list);
      
      if (list.length > 0) {
        // Restore last-selected website from localStorage if available and still valid
        let pick = null;
        try {
          const lastId = parseInt(localStorage.getItem('kip_last_website') || '');
          if (lastId && list.find(w => w.id === lastId)) {
            pick = lastId;
          }
        } catch {}
        if (!pick) pick = list[0].id;
        setCurrentWebsite(pick);
      }
    } catch (error) {
      console.error('Error loading websites:', error);
    } finally {
      setLoading(false);
    }
  };

  // Persist current website to localStorage whenever it changes
  React.useEffect(() => {
    if (currentWebsite) {
      try { localStorage.setItem('kip_last_website', String(currentWebsite)); } catch {}
    }
  }, [currentWebsite]);

  const handleSync = async () => {
    if (!currentWebsite) {
      alert('Please select a website first');
      return;
    }

    if (!window.confirm('Sync last 28 days of keyword data?')) {
      return;
    }

    setSyncing(true);
    
    let dispatch;
    try {
      dispatch = await api.syncWebsite(currentWebsite);
    } catch (error) {
      setSyncing(false);
      const errData = error.response?.data;
      if (errData?.action === 'reconnect_google') {
        const email = errData.google_email || 'this account';
        const doReconnect = window.confirm(
          `Google account (${email}) needs to be reconnected.\n\n` +
          `Google revoked the access token. Click OK to reconnect now, or Cancel to do it later.`
        );
        if (doReconnect) {
          try {
            const authData = await api.getOAuthUrl(window.location.pathname);
            if (authData.auth_url) {
              window.location.href = authData.auth_url;
              return;
            }
            alert('Could not get reconnect URL from server.');
          } catch (e) {
            alert('Reconnect failed to start: ' + e.message);
          }
        }
      } else {
        alert(`❌ Sync Failed to Start\n\n${errData?.message || errData?.error || error.message}`);
      }
      return;
    }
    
    // Sync dispatched, poll for completion
    const syncLogId = dispatch?.sync_log_id;
    const websiteId = currentWebsite;
    const startedAt = Date.now();
    const MAX_MS = 10 * 60 * 1000; // 10 minutes
    
    const poll = async () => {
      try {
        const statusResp = await api.getSyncStatus(websiteId);
        const recent = (statusResp.recent_syncs || []).find(s => s.id === syncLogId)
                    || (statusResp.recent_syncs || [])[0];
        
        if (!recent) return true;
        
        if (recent.status === 'completed') {
          setSyncing(false);
          alert(`✅ Sync Complete!\n\nRows Fetched: ${recent.rows_fetched || 0}\nKeywords Added: ${recent.rows_inserted || 0}\nKeywords Updated: ${recent.rows_updated || 0}\nDuration: ${recent.duration_seconds || 0}s`);
          window.location.reload();
          return false;
        }
        
        if (recent.status === 'failed') {
          setSyncing(false);
          alert(`❌ Sync Failed\n\n${recent.error_message || 'Unknown error'}`);
          return false;
        }
        
        if (Date.now() - startedAt > MAX_MS) {
          setSyncing(false);
          alert('Sync is still running after 10 minutes. Check back later; it will continue in the background.');
          return false;
        }
        return true;
      } catch (e) {
        console.error('sync poll error', e);
        return true;
      }
    };
    
    const interval = setInterval(async () => {
      const shouldContinue = await poll();
      if (!shouldContinue) clearInterval(interval);
    }, 3000);
    
    setTimeout(async () => {
      const shouldContinue = await poll();
      if (!shouldContinue) clearInterval(interval);
    }, 1000);
  };

  const handleLogout = () => {
    api.logout();
    onLogout();
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <header className="header">
        <div className="header-container">
          <div className="logo">
            <span className="logo-icon">🎯</span>
            <div className="logo-text">
              <h1>Keyword Intelligence</h1>
              <p>Professional SEO Analytics</p>
            </div>
          </div>

          <div className="header-actions">
            <select
              className="website-selector"
              value={currentWebsite || ''}
              onChange={(e) => setCurrentWebsite(parseInt(e.target.value))}
            >
              {websites.length === 0 ? (
                <option>No websites - Add one in Settings</option>
              ) : (
                websites.map(w => (
                  <option key={w.id} value={w.id}>{w.display_name}</option>
                ))
              )}
            </select>

            <select
              value={globalDays}
              onChange={(e) => setGlobalDays(parseInt(e.target.value))}
              style={{
                padding: '8px 12px',
                fontSize: 13,
                fontWeight: 600,
                border: '1.5px solid rgba(255,255,255,0.3)',
                borderRadius: 8,
                background: 'rgba(255,255,255,0.15)',
                color: '#fff',
                cursor: 'pointer',
                outline: 'none',
              }}
              title="Date range — applies to all data on this page"
            >
              <option value={7} style={{ color: '#000' }}>Last 7 days</option>
              <option value={28} style={{ color: '#000' }}>Last 28 days</option>
              <option value={90} style={{ color: '#000' }}>Last 90 days</option>
              <option value={180} style={{ color: '#000' }}>Last 6 months</option>
              <option value={365} style={{ color: '#000' }}>Last 12 months</option>
            </select>

            <button 
              className="btn-success" 
              onClick={handleSync}
              disabled={syncing || !currentWebsite}
            >
              {syncing ? '⏳ Syncing...' : '🔄 Sync Data'}
            </button>

            <button className="btn-secondary" onClick={handleLogout}>
              👋 Logout
            </button>
          </div>
        </div>
      </header>

      <BackfillBanner />

      <nav className="nav-tabs">
        <NavLink to="/overview" className={({ isActive }) => isActive ? 'active' : ''}>
          📊 Overview
        </NavLink>
        <NavLink to="/opportunities" className={({ isActive }) => isActive ? 'active' : ''}>
          🎯 Opportunities
        </NavLink>
        <NavLink to="/brief" className={({ isActive }) => isActive ? 'active' : ''}>
          🧭 Brief
        </NavLink>
        <NavLink to="/report" className={({ isActive }) => isActive ? 'active' : ''}>
          📄 Report
        </NavLink>
        <NavLink to="/funnel" className={({ isActive }) => isActive ? 'active' : ''}>
          📈 Funnel
        </NavLink>
        <NavLink to="/client-report" className={({ isActive }) => isActive ? 'active' : ''}>
          📊 Client Report
        </NavLink>
        <NavLink to="/keywords" className={({ isActive }) => isActive ? 'active' : ''}>
          📋 Keywords
        </NavLink>
        <NavLink to="/history" className={({ isActive }) => isActive ? 'active' : ''}>
          📈 History
        </NavLink>
        <NavLink to="/competitors" className={({ isActive }) => isActive ? 'active' : ''}>
          🎯 Competitors
        </NavLink>
        <NavLink to="/site-audit" className={({ isActive }) => isActive ? 'active' : ''}>
          🛡️ Site Audit
        </NavLink>
        <NavLink to="/settings" className={({ isActive }) => isActive ? 'active' : ''}>
          ⚙️ Settings
        </NavLink>
      </nav>

      <main className="main-content">
        <Routes>
          <Route path="/brief" element={<Brief websiteId={currentWebsite} globalDays={globalDays} />} />
          <Route path="/report" element={<MonthlyReport websiteId={currentWebsite} />} />
          <Route path="/funnel" element={<FunnelReport websiteId={currentWebsite} />} />
          <Route path="/client-report" element={<CombinedReport websiteId={currentWebsite} />} />
          <Route path="/" element={
            websites.length === 0
              ? <WelcomePanel onAddSite={() => navigate('/settings')} />
              : <Overview websiteId={currentWebsite} days={globalDays} />
          } />
          <Route path="/overview" element={
            websites.length === 0
              ? <WelcomePanel onAddSite={() => navigate('/settings')} />
              : <Overview websiteId={currentWebsite} days={globalDays} />
          } />
          <Route path="/opportunities" element={
            websites.length === 0
              ? <WelcomePanel onAddSite={() => navigate('/settings')} />
              : <Opportunities websiteId={currentWebsite} />
          } />
          <Route path="/keywords" element={
            websites.length === 0
              ? <WelcomePanel onAddSite={() => navigate('/settings')} />
              : <Keywords websiteId={currentWebsite} globalDays={globalDays} />
          } />
          <Route path="/history" element={
            websites.length === 0
              ? <WelcomePanel onAddSite={() => navigate('/settings')} />
              : <RankingHistory websiteId={currentWebsite} />
          } />
          <Route path="/competitors" element={
            websites.length === 0
              ? <WelcomePanel onAddSite={() => navigate('/settings')} />
              : <Competitors websiteId={currentWebsite} />
          } />
          <Route path="/site-audit" element={
            websites.length === 0
              ? <WelcomePanel onAddSite={() => navigate('/settings')} />
              : <SiteAudit websiteId={currentWebsite} websiteDomain={(websites.find(w => w.id === currentWebsite) || {}).display_name || ''} />
          } />
          <Route path="/settings" element={<Settings websites={websites} onWebsitesChange={loadWebsites} />} />
        </Routes>
      </main>
    </div>
  );
}


// ── First-run welcome panel ─────────────────────────────────────────────────
function WelcomePanel({ onAddSite }) {
  return (
    <div style={{ maxWidth: 640, margin: '60px auto', textAlign: 'center', padding: '0 20px' }}>
      <div style={{ fontSize: 56, marginBottom: 20 }}>🎯</div>
      <h1 style={{ fontSize: 28, fontWeight: 700, color: '#0f172a', marginBottom: 12, letterSpacing: '-0.02em' }}>
        Welcome to KeywordIP
      </h1>
      <p style={{ fontSize: 16, color: '#64748b', marginBottom: 32, lineHeight: 1.5 }}>
        Get started by adding your first website. We will connect to Google Search Console,
        pull your last 28 days of keywords, and run a site audit so you can see what is working
        and what is not.
      </p>
      <button onClick={onAddSite}
        style={{
          padding: '14px 28px', fontSize: 15, fontWeight: 600,
          background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
          color: '#fff', border: 'none', borderRadius: 10,
          cursor: 'pointer', boxShadow: '0 4px 12px rgba(99,102,241,0.3)',
        }}>
        + Add Your First Site
      </button>
      <div style={{ marginTop: 40, padding: 20, background: '#f8fafc', borderRadius: 12, border: '1px solid #e2e8f0', textAlign: 'left' }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 10 }}>
          What you will see once a site is added
        </div>
        <ul style={{ fontSize: 13, color: '#475569', lineHeight: 1.8, paddingLeft: 18, margin: 0 }}>
          <li>Real Google traffic numbers — clicks, impressions, click rate</li>
          <li>Which keywords are bringing real clicks vs which are vanity rankings</li>
          <li>Lost opportunities — keywords getting impressions but no clicks</li>
          <li>Site audit — 25+ technical and content checks across your pages</li>
        </ul>
      </div>
    </div>
  );
}

export default Dashboard;
